import React from 'react';

import { useTranslation } from 'react-i18next';
import cx from 'classnames';

import { FONT_AWESOME_SOLID, ICON_SET_FONTAWESOME } from 'constants-activate';
import { getDisplayName } from 'lane-shared/helpers';
import { getInitials, imageUrl } from 'lane-shared/helpers/formatters';

import { Channel } from 'application-types';
import { AVATAR_COLOR_COUNT } from 'constants-channel';
import { P, Icon } from 'design-system-web';

import styles from './HomeChannelPickerModal.scss';

function getAvatarColorIndex(name: string): number {
  let hash = 0;

  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }

  return Math.abs(hash) % AVATAR_COLOR_COUNT;
}

type ChannelAvatarProps = {
  name: string;
  logo?: string | null;
};

export function ChannelAvatar({ name, logo }: ChannelAvatarProps) {
  const colorClass = styles[`avatarColor${getAvatarColorIndex(name)}`];

  if (logo) {
    return (
      <div className={styles.avatar}>
        <img
          src={imageUrl(logo)}
          alt={name}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            borderRadius: 'inherit',
          }}
        />
      </div>
    );
  }

  return (
    <div className={cx(styles.avatar, colorClass)}>
      {getInitials(name, true)}
    </div>
  );
}

export type ChannelPickerRowProps = {
  channel: Channel;
  isTenantRow?: boolean;
  isSelected: boolean;
  isHome: boolean;
  tenantCount?: number;
  isExpanded?: boolean;
  onSelect: () => void;
  onToggleExpand?: () => void;
};

export function ChannelPickerRow({
  channel,
  isTenantRow,
  isSelected,
  isHome,
  tenantCount,
  isExpanded,
  onSelect,
  onToggleExpand,
}: ChannelPickerRowProps) {
  const { t } = useTranslation();
  const displayName = getDisplayName(channel);
  const hasExpandToggle = !isTenantRow && onToggleExpand;

  function handleChevronClick(e: React.MouseEvent) {
    e.stopPropagation();
    onToggleExpand?.();
  }
  
  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onSelect();
    }
  }

  return (
    <div
      className={cx(styles.row, isTenantRow && styles.tenantRow, {
        [styles.selected]: isSelected,
      })}
      onClick={onSelect}
      onKeyDown={handleKeyDown}
      role="button"
      tabIndex={0}
      data-test={`homeChannelOption-${channel._id}`}
    >
      {hasExpandToggle ? (
        <button
          className={styles.chevron}
          onClick={handleChevronClick}
          tabIndex={-1}
          type="button"
        >
          <Icon
            name={isExpanded ? 'chevron-down' : 'chevron-right'}
            set={ICON_SET_FONTAWESOME}
            type="far"
          />
        </button>
      ) : (
        <div className={styles.chevronPlaceholder} />
      )}

      <ChannelAvatar name={displayName} logo={channel.profile?.logo} />

      <div className={styles.rowLabel}>
        <P>{displayName}</P>
        <span
          className={cx(
            styles.badge,
            isTenantRow ? styles.badgeTenant : styles.badgeProperty
          )}
        >
          {isTenantRow
            ? t('web.userProfile.homeChannel.tenant')
            : t('web.userProfile.homeChannel.property')}
        </span>
        {!isTenantRow && Boolean(tenantCount) && (
          <span className={styles.badge}>
            {tenantCount===1
              ? t('web.userProfile.homeChannel.tenantCountSingle', { count: tenantCount })
              : t('web.userProfile.homeChannel.tenantCountMultiple', { count: tenantCount })}
          </span>
        )}
      </div>

      {(isHome || isSelected) && (
        <Icon
          className={styles.homeIcon}
          name="home"
          set={ICON_SET_FONTAWESOME}
          type={FONT_AWESOME_SOLID}
        />
      )}
    </div>
  );
}
